import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { changePassword, fetchCurrentUser, fetchVisits } from '../api/api';
import {
  FiUser,
  FiMail,
  FiShield,
  FiPhone,
  FiLogOut,
  FiMapPin,
  FiHome,
  FiBox,
  FiHash,
  FiGlobe,
  FiLock,
  FiSettings,
  FiCalendar,
  FiCheckCircle,
  FiSmartphone,
  FiMonitor,
  FiClock,
  FiChevronRight,
} from 'react-icons/fi';
import { IoBusiness, IoCar, IoLayers, IoLocation, IoCreate, IoHardwareChip } from 'react-icons/io5';
import './Profile.css';

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCurrentUser();
      setProfile(data);
    } catch {
      setProfile(user);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const loadVisits = useCallback(async () => {
    const displayUser = profile || user;
    if (!displayUser?.id && !displayUser?.user_id) return;
    if (displayUser?.site_type !== 'AMC' && displayUser?.siteType !== 'AMC') return;
    
    setVisitsLoading(true);
    try {
      const visitsData = await fetchVisits({ userId: displayUser?.id || displayUser?.user_id, limit: 3 });
      setVisits(Array.isArray(visitsData) ? visitsData.slice(0, 3) : []);
    } catch (err) {
      setVisits([]);
    } finally {
      setVisitsLoading(false);
    }
  }, [profile, user]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    loadVisits();
  }, [loadVisits]);

  const displayUser = profile || user;
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changing, setChanging] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Parse system types with quantities
  const getSystemTypes = () => {
    if (!displayUser) return [];
    const types = displayUser.system_types || displayUser.systemTypes || [];
    const quantities = displayUser.system_type_quantities || displayUser.systemTypeQuantities || {};
    
    if (Array.isArray(types) && types.length > 0) {
      return types.map(type => ({
        name: type,
        quantity: quantities[type] || 1
      }));
    }
    if (typeof types === 'string') {
      try {
        const parsed = JSON.parse(types);
        if (Array.isArray(parsed)) {
          return parsed.map(type => ({
            name: type,
            quantity: quantities[type] || 1
          }));
        }
      } catch {
        return [{ name: types, quantity: 1 }];
      }
    }
    return [];
  };

  const systemTypes = getSystemTypes();

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || newPassword.length < 6) {
      alert('Please fill all fields. New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      alert('New password and confirm password do not match.');
      return;
    }
    setChanging(true);
    try {
      await changePassword(currentPassword, newPassword);
      alert('Password changed successfully.');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      alert(err.message || 'Failed to change password.');
    } finally {
      setChanging(false);
    }
  };

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    logout();
    window.location.href = '/login';
  };

  const getRoleColor = (role) => {
    const r = (role || displayUser?.role || '').toLowerCase();
    if (r === 'admin') return '#FF9800';
    if (r === 'engineer') return '#4CAF50';
    if (r === 'area_head' || r === 'area head') return '#2196F3';
    return '#F57C00';
  };

  const getRoleLabel = (role) => {
    if (role === 'area_head' || role === 'areaHead') return 'Area Head';
    return (role || displayUser?.role || 'User').charAt(0).toUpperCase() + (role || displayUser?.role || 'User').slice(1).toLowerCase();
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="profile-screen">
        <div className="profile-loading-container">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  const isAMC = displayUser?.site_type === 'AMC' || displayUser?.siteType === 'AMC';
  const hasSiteInfo = displayUser?.site_name || displayUser?.siteName || displayUser?.site_address || displayUser?.siteAddress;

  return (
    <div className="profile-screen">
      {/* Profile Header with Gradient */}
      <div className="profile-header">
        <div className="profile-avatar">
          <span className="profile-avatar-text">{(displayUser?.name || displayUser?.email || 'U').charAt(0).toUpperCase()}</span>
        </div>
        <div className="profile-name">{displayUser?.name || 'User'}</div>
        <div className="profile-role-badge" style={{ background: getRoleColor() }}>
          <FiShield style={{ marginRight: '6px', fontSize: '12px' }} />
          {getRoleLabel()}
        </div>
        <div className="profile-email">{displayUser?.email || ''}</div>
      </div>

      {/* Account Status Badge */}
      <div className="profile-status-badge active">
        <FiCheckCircle /> Active Account
      </div>

      {/* DEBUG - Remove after fixing */}
      {process.env.NODE_ENV === 'development' && (
        <details style={{ marginBottom: '20px', padding: '10px', background: '#f5f5f5', borderRadius: '8px', fontSize: '12px' }}>
          <summary>Debug: Raw User Data</summary>
          <pre style={{ overflow: 'auto', maxHeight: '200px' }}>
            {JSON.stringify(displayUser, null, 2)}
          </pre>
        </details>
      )}

      {/* Account Details Section - 7 Always Visible Fields */}
      <div className="profile-section" style={{ display: 'block' }}>
        <div className="section-header-row">
          <h2 className="section-title">
            <FiUser /> Account Details
          </h2>
        </div>
        <div className="profile-detail-box">
          {/* Name */}
          <div className="profile-field-card" style={{ display: 'flex' }}>
            <div className="profile-field-icon-wrap"><FiUser /></div>
            <div className="profile-field-content">
              <span className="profile-field-label">Name</span>
              <span className="profile-field-value">{displayUser?.name || displayUser?.email?.split('@')[0] || '—'}</span>
            </div>
          </div>
          {/* Email */}
          <div className="profile-field-card" style={{ display: 'flex' }}>
            <div className="profile-field-icon-wrap"><FiMail /></div>
            <div className="profile-field-content">
              <span className="profile-field-label">Email</span>
              <span className="profile-field-value">{displayUser?.email || '—'}</span>
            </div>
          </div>
          {/* Phone */}
          <div className="profile-field-card" style={{ display: 'flex' }}>
            <div className="profile-field-icon-wrap"><FiPhone /></div>
            <div className="profile-field-content">
              <span className="profile-field-label">Phone</span>
              <span className="profile-field-value">{displayUser?.phone || 'Not provided'}</span>
            </div>
          </div>
          {/* User ID */}
          <div className="profile-field-card" style={{ display: 'flex' }}>
            <div className="profile-field-icon-wrap"><FiHash /></div>
            <div className="profile-field-content">
              <span className="profile-field-label">User ID</span>
              <span className="profile-field-value">{displayUser?.user_id || displayUser?.userId || displayUser?.id || '—'}</span>
            </div>
          </div>
          {/* Role */}
          <div className="profile-field-card" style={{ display: 'flex' }}>
            <div className="profile-field-icon-wrap"><FiShield /></div>
            <div className="profile-field-content">
              <span className="profile-field-label">Role</span>
              <span className="profile-field-value">{getRoleLabel() || '—'}</span>
            </div>
          </div>
          {/* Member Since */}
          <div className="profile-field-card" style={{ display: 'flex' }}>
            <div className="profile-field-icon-wrap"><FiCalendar /></div>
            <div className="profile-field-content">
              <span className="profile-field-label">Member Since</span>
              <span className="profile-field-value">{formatDate(displayUser?.created_at || displayUser?.createdAt)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Site Details Section */}
      {hasSiteInfo && (
        <div className="profile-section">
          <div className="section-header-row">
            <h2 className="section-title">
              <IoBusiness /> Site Details
            </h2>
          </div>
          <div className="profile-detail-box">
            <div className="profile-field-card">
              <div className="profile-field-icon-wrap">
                <IoBusiness />
              </div>
              <div className="profile-field-content">
                <span className="profile-field-label">Site Name</span>
                <span className="profile-field-value">{displayUser?.site_name || displayUser?.siteName || '—'}</span>
              </div>
            </div>
            <div className="profile-field-card">
              <div className="profile-field-icon-wrap">
                <FiHome />
              </div>
              <div className="profile-field-content">
                <span className="profile-field-label">Site Address</span>
                <span className="profile-field-value">{displayUser?.site_address || displayUser?.siteAddress || '—'}</span>
              </div>
            </div>
            <div className="profile-field-card">
              <div className="profile-field-icon-wrap">
                <FiBox />
              </div>
              <div className="profile-field-content">
                <span className="profile-field-label">Site Type</span>
                <span className={`profile-field-value site-type ${(displayUser?.site_type || displayUser?.siteType || '').toLowerCase()}`}>
                  {displayUser?.site_type || displayUser?.siteType || '—'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Systems Section with Quantity Chips */}
      {(systemTypes.length > 0 || displayUser?.total_systems || displayUser?.totalSystems) && (
        <div className="profile-section">
          <div className="section-header-row">
            <h2 className="section-title">
              <IoHardwareChip /> Systems
            </h2>
            {(displayUser?.total_systems || displayUser?.totalSystems) && (
              <span className="systems-total-badge">
                Total: {displayUser?.total_systems || displayUser?.totalSystems}
              </span>
            )}
          </div>
          <div className="profile-detail-box">
            {systemTypes.length > 0 && (
              <div className="system-types-chips">
                {systemTypes.map((type, idx) => (
                  <span key={idx} className="system-type-chip">
                    {type.name}
                    <span className="quantity-badge">x{type.quantity}</span>
                  </span>
                ))}
              </div>
            )}
            {(displayUser?.car_count || displayUser?.carCount) && (
              <div className="profile-field-card" style={{ marginTop: '12px' }}>
                <div className="profile-field-icon-wrap">
                  <IoCar />
                </div>
                <div className="profile-field-content">
                  <span className="profile-field-label">Car Count</span>
                  <span className="profile-field-value">{displayUser?.car_count || displayUser?.carCount}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Location Section */}
      {(displayUser?.state || displayUser?.area) && (
        <div className="profile-section">
          <div className="section-header-row">
            <h2 className="section-title">
              <IoLocation /> Location
            </h2>
          </div>
          <div className="profile-detail-box">
            {displayUser?.state && (
              <div className="profile-field-card">
                <div className="profile-field-icon-wrap">
                  <FiGlobe />
                </div>
                <div className="profile-field-content">
                  <span className="profile-field-label">State</span>
                  <span className="profile-field-value">{displayUser.state}</span>
                </div>
              </div>
            )}
            {displayUser?.area && (
              <div className="profile-field-card">
                <div className="profile-field-icon-wrap">
                  <FiMapPin />
                </div>
                <div className="profile-field-content">
                  <span className="profile-field-label">Area / District</span>
                  <span className="profile-field-value">{displayUser.area}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Active Devices Section */}
      <div className="profile-section">
        <div className="section-header-row">
          <h2 className="section-title">
            <FiSmartphone /> Active Devices
          </h2>
          <span className="device-count-badge">{activeSessions.length}/2</span>
        </div>
        <div className="profile-detail-box devices-box">
          {sessionsLoading ? (
            <div className="devices-loading">Loading devices...</div>
          ) : activeSessions.length === 0 ? (
            <div className="empty-devices">
              <FiMonitor size={32} />
              <span>No active sessions</span>
            </div>
          ) : (
            activeSessions.map((session, index) => {
              const isCurrentDevice = index === 0; // First device is current
              const deviceName = session.device_name || 'Web Browser';
              const createdAt = formatDate(session.created_at);
              return (
                <div key={session.id || index} className={`device-row ${isCurrentDevice ? 'current' : ''}`}>
                  <div className="device-icon-wrap">
                    <FiMonitor />
                  </div>
                  <div className="device-info">
                    <span className="device-name">{deviceName}</span>
                    <span className="device-date">Logged in: {createdAt}</span>
                  </div>
                  {isCurrentDevice && (
                    <span className="current-device-badge">
                      <FiCheckCircle /> This Device
                    </span>
                  )}
                </div>
              );
            })
          )}
          <p className="device-limit-note">Maximum 2 devices allowed</p>
        </div>
      </div>

      {/* My Visits Button - AMC Users Only */}
      {isAMC && (
        <div className="profile-section">
          <button className="profile-btn visits" onClick={() => navigate('/visits')}>
            <FiCalendar /> My Visits
            <FiChevronRight className="btn-arrow" />
          </button>
        </div>
      )}

      {/* Security Section */}
      <div className="profile-section">
        <div className="section-header-row">
          <h2 className="section-title">
            <FiLock /> Security
          </h2>
        </div>
        <button className="profile-btn security" onClick={() => setShowChangePassword(true)}>
          <div className="security-icon-wrap">
            <FiLock />
          </div>
          <div className="security-text">
            <span className="security-title">Change Password</span>
            <span className="security-subtitle">Update your account password</span>
          </div>
          <FiChevronRight className="btn-arrow" />
        </button>
      </div>

      {/* Logout Button */}
      <button className="profile-btn logout" onClick={() => setShowLogoutConfirm(true)}>
        <div className="logout-icon-wrap">
          <FiLogOut />
        </div>
        <span className="logout-text">Logout</span>
        <FiChevronRight className="btn-arrow" />
      </button>

      {showChangePassword && (
        <div className="profile-modal-overlay" onClick={() => !changing && setShowChangePassword(false)}>
          <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Change Password</h3>
            <form onSubmit={handleChangePassword}>
              <input
                type="password"
                placeholder="Current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={changing}
              />
              <input
                type="password"
                placeholder="New password (min 6 chars)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={changing}
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={changing}
              />
              <div className="profile-modal-actions">
                <button type="button" onClick={() => setShowChangePassword(false)} disabled={changing}>Cancel</button>
                <button type="submit" disabled={changing}>{changing ? 'Updating...' : 'Update'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showLogoutConfirm && (
        <div className="profile-modal-overlay" onClick={() => setShowLogoutConfirm(false)}>
          <div className="profile-modal profile-confirm" onClick={(e) => e.stopPropagation()}>
            <h3>Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div className="profile-modal-actions">
              <button type="button" onClick={() => setShowLogoutConfirm(false)}>Cancel</button>
              <button type="button" className="logout-confirm" onClick={handleLogout}>Logout</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
